import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';

import { TranslocoService, translate } from '@jsverse/transloco';

import { Subject, combineLatest, takeUntil } from 'rxjs';

import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import {
  ConfigurationRoute,
  ConfigurationRouteService,
} from 'app/providers/semantria-api/configuration-route.service';
import {
  SemantriaConfiguration,
  ConfigurationService,
} from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  IndustryPack,
  IndustryPackService,
} from 'app/providers/semantria-api/industry-pack.service';
import {
  LanguageTemplate,
  LanguageTemplateService,
} from 'app/providers/semantria-api/language-template.service';
import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import { AlertService } from 'app/services/alert.service';

import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ConfigurationRouteListService } from '../../services/configuration-route-list.service';

export interface ConfigurationListEntry {
  languageId: string;
  languageName: string;
  configurations: { pillType: string; configuration: SemantriaConfiguration }[];
}

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-route-configurations',
  templateUrl: './configuration-route-configurations.component.html',
  styleUrls: ['./configuration-route-configurations.component.scss'],
  standalone: false,
})
export class ConfigurationRouteConfigurationsComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() configurationRoute: ConfigurationRoute;
  @Input() canEditRoute: boolean;

  @Output() configurationRouteChange = new EventEmitter<ConfigurationRoute>();

  isLoading;
  isSaving: boolean;

  private session: Session;
  includedConfigurations: ConfigurationListEntry[] = [];

  hasTranslationFeature;
  groupId: string;
  selectedConfiguration: SemantriaConfiguration;
  selectedConfigurationType: string;
  templateConfiguration: SemantriaConfiguration;
  configurations: SemantriaConfiguration[];
  industryPacks: IndustryPack[];
  languageTemplates: LanguageTemplate[];
  readonly typeButtons = [
    { label: 'Entities', clickHandler: () => this.gotoConfigurationPage('entities') },
    { label: 'Sentiment', clickHandler: () => this.gotoConfigurationPage('sentiment') },
    { label: 'Queries', clickHandler: () => this.gotoConfigurationPage('queries') },
    { label: 'Concept Topics', clickHandler: () => this.gotoConfigurationPage('concept_topics') },
    { label: 'Taxonomy', clickHandler: () => this.gotoConfigurationPage('taxonomy') },
    { label: 'Blacklist', clickHandler: () => this.gotoConfigurationPage('blacklist') },
  ];
  configurationLimits = {
    Entities: {
      used: -1,
      total: 0,
    },
    Sentiment: {
      used: -1,
      total: 0,
    },
    Queries: {
      used: -1,
      total: 0,
    },
    'Concept Topics': {
      used: -1,
      total: 0,
    },
    Taxonomy: {
      used: -1,
      total: 0,
    },
    Blacklist: {
      used: -1,
      total: 0,
    },
  };
  accountLimits: { [key: string]: number };

  showAnalyticItemDialog;
  showMachineTranslationDialog;
  showEditConfigurationsModal: boolean;
  editConfigurationsDialogData;

  editLinkText;
  selectionCountText;

  machineTranslationTooltips = [];

  private unsub$ = new Subject<void>();

  constructor(
    private groupService: GroupService,
    private configurationService: ConfigurationService,
    private configurationRouteService: ConfigurationRouteService,
    private semantriaAccountService: SemantriaAccountService,
    private industryPackService: IndustryPackService,
    private languageTemplateService: LanguageTemplateService,
    private alertService: AlertService,
    private listService: ConfigurationRouteListService,
    private dialogService: DialogService,
    private authService: AuthService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.groupId = this.groupService.getSelectedGroupId(true);
    Promise.all([
      this.semantriaAccountService.sub.latest(),
      this.configurationService.getConfigurations(true, this.groupService.getSelectedGroupId()),
      this.industryPackService.getIndustryPacks(),
      this.languageTemplateService.getLanguageTemplates(),
      this.authService.getSession(),
    ])
      .then((res) => {
        this.accountLimits = res[0].limits;
        this.hasTranslationFeature = res[0].features.includes('TRANSLATION');

        this.configurations = res[1];
        this.industryPacks = res[2];
        this.languageTemplates = res[3];
        this.session = res[4];

        this.assembleIncludedConfigurationsList();
      })
      .finally(() => (this.isLoading = false));

    combineLatest([
      this.translocoService.selectTranslate(
        'configuration-routes.view.configurations-tab.machine-language-autoresponse'
      ),
      this.translocoService.selectTranslate('configuration-routes.list.machine-language'),
    ])
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => (this.machineTranslationTooltips = translations));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.configurationRoute?.currentValue && this.configurations) {
      this.selectedConfiguration = undefined;

      this.assembleIncludedConfigurationsList();
    }
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  private assembleIncludedConfigurationsList() {
    this.includedConfigurations = Object.entries(this.configurationRoute.configuration_map).map(
      ([langId, configId]) => {
        let configuration = this.configurations.find((c) => c.id === configId) as any;
        let pillType = 'CO' as any;
        if (!configuration) {
          configuration = this.industryPacks.find((p) => p.id === configId);
          pillType = 'IP';
        }
        if (!configuration) {
          configuration = this.languageTemplates.find((lt) => lt.id === configId);
          pillType = 'LT';
        }
        return {
          languageId: langId,
          languageName: configuration.language_name,
          configurations: [
            {
              configuration,
              pillType,
            },
          ],
        };
      }
    );

    this.editLinkText = this.includedConfigurations.length
      ? translate('configuration-routes.view.configurations-tab.edit')
      : translate('configuration-routes.view.configurations-tab.add');
    this.selectionCountText = translate(
      'configuration-routes.view.configurations-tab.selection-count',
      { count: this.includedConfigurations.length }
    );

    this.includedConfigurations.sort((c1, c2) => c1.languageName.localeCompare(c2.languageName));

    if (!this.selectedConfiguration && this.includedConfigurations.length) {
      this.onConfigurationSelected(this.includedConfigurations[0].configurations[0]);
    }
  }

  toggleDefaultLanguage(language: string, $event) {
    if (this.canEditRoute) {
      $event.detail.nativeEvent.stopImmediatePropagation();

      const oldValue = this.configurationRoute.default_language_id;

      this.configurationRoute.default_language_id = language;

      this.updateConfigurationRoute(
        'default_language_id',
        translate('configuration-routes.list.default-language'),
        language,
        oldValue
      );
    }
  }

  get canToggleMachineTranslation() {
    return this.configurationRoute.response_type !== 'AUTORESPONSE';
  }

  get isReadOnly() {
    return this.authService.isReadOnly;
  }

  getMachineTranslationTooltip(entry: ConfigurationListEntry): string {
    if (!this.canToggleMachineTranslation) {
      return this.machineTranslationTooltips[0];
    }

    return this.configurationRoute.translation_target_language_id === entry.languageId
      ? ''
      : this.machineTranslationTooltips[1];
  }

  toggleMachineTranslation(language: string, $event) {
    if (this.canEditRoute) {
      $event.stopImmediatePropagation();

      if (this.canToggleMachineTranslation) {
        const oldValue = this.configurationRoute.translation_target_language_id;
        const newValue = oldValue === language ? '' : language;

        this.configurationRoute.translation_target_language_id = newValue;

        this.updateConfigurationRoute(
          'translation_target_language_id',
          translate('configuration-routes.list.machine-language'),
          newValue,
          oldValue
        );
      }
    }
  }

  private updateConfigurationRoute(
    propName: string,
    propLabel: string,
    language: string,
    oldValue: string
  ) {
    const languageName =
      language.toUpperCase() || translate('configuration-routes.view.configurations-tab.nothing');
    this.isSaving = true;
    this.configurationRouteService
      .updateConfigurationRoute(this.configurationRoute)
      .then((res) => {
        this.alertService.showToast(
          translate('configuration-routes.view.configurations-tab.update-success', {
            propLabel,
            languageName,
          })
        );
        this.listService.routeUpdated(res);
      })
      .catch((err) => {
        this.configurationRoute[propName] = oldValue;
        this.alertService.showErrorToast(
          translate('configuration-routes.view.configurations-tab.update-failed', {
            propLabel,
            languageName,
          }),
          err
        );
      })
      .finally(() => (this.isSaving = false));
  }

  onConfigurationSelected(entry: { pillType: string; configuration: SemantriaConfiguration }) {
    this.selectedConfiguration = entry.configuration;
    this.selectedConfigurationType = entry.pillType;

    if (this.selectedConfiguration.cloned_from) {
      this.configurationService
        .getConfiguration(this.selectedConfiguration.cloned_from)
        .then((res) => {
          if (res) {
            this.templateConfiguration = res;
          }
        })
        .catch(() => {
          //This is fine just means it's an industry pack or language template so will get filled in inside
          //the configuration-overview.component.
        });
    } else {
      this.templateConfiguration = undefined;
    }
  }

  gotoConfigurationPage(fragment: string) {
    window.open(
      '/groups/' +
        this.groupId +
        '/configurations/' +
        this.selectedConfiguration.id +
        '#' +
        fragment,
      '_blank'
    );
  }

  onAnalyticItemDialogOpened() {
    this.showAnalyticItemDialog = true;
  }

  onAnalyticItemDialogClosed() {
    this.showAnalyticItemDialog = false;
  }

  onEditConfigurationDialogOpened() {
    this.editConfigurationsDialogData = {
      configurationRoute: this.configurationRoute,
    };
    this.showEditConfigurationsModal = true;
  }

  onEditConfigurationsModalClosed(response) {
    this.showEditConfigurationsModal = false;
    if (response) {
      this.configurationRoute = response;

      if (
        this.selectedConfiguration &&
        !Object.values(this.configurationRoute.configuration_map).includes(
          this.selectedConfiguration.id
        )
      ) {
        this.selectedConfiguration = undefined;
      }

      this.listService.routeUpdated(this.configurationRoute);

      this.assembleIncludedConfigurationsList();
    }
  }
}
